<template>
<div class="invoice-wrapper">
	<div class="invoice" id="invoice" v-if="order">
		<div class="invoice__body">
			<!-- Header -->
			<div class="invoice__header">
				<div class="invoice__image">
					<!-- <div class="is-loading" v-if="!logo"></div> -->
					<img
						class="invoice__logo"
						crossorigin="anonymous"
						:src="logo"
						:alt="storeName"
						@load="onLogoLoaded"
						v-if="logo">
				</div>
				<div class="invoice__info">
					<div class="invoice__title">Invoice #<span class="invoice__id">{{ order.order_id }}</span></div>
					<div class="invoice__date">
						<span class="invoice-order-status mr-1" :class="`status-${status}`">
							<span>({{ status }})</span>
						</span>
						<span>Order Date: </span>
						<span class="invoice__date-value">{{ orderDate }}</span>
					</div>
				</div>
			</div>

			<!-- Customer -->
			<div class="invoice__customer">
				<div class="invoice__customer__top">
					<div class="invoice__customer__name">{{ customerName }}</div>
					<div class="invoice__status">
						<div class="invoice-status" :class="paymentStatus">{{ paymentStatus }}</div>
					</div>
				</div>
				<div>
					<!-- Email -->
					<div class="invoice__customer__item invoice__customer__email" v-if="customerEmail"><i class="material-icons md-sm">email</i><a target="_blank" :href="`mailto:${customerEmail}`">{{ customerEmail }}</a></div>
					<!-- Phone -->
					<div class="invoice__customer__item invoice__customer__phone" v-if="customerPhone"><i class="material-icons md-sm">phone</i><span>{{ customerPhone }}</span></div>
					<!-- Address -->
					<div class="invoice__customer__item invoice__customer__address">
						<i class="material-icons md-sm">location_city</i>
						<div>
							<div class="invoice__customer__street" v-if="customerAddress">{{ customerAddress }}</div>
							<div class="invoice__customer__city">{{ customerCity }}</div>
						</div>
					</div>
					<!-- Notes -->
					<div class="invoice__customer__item invoice__customer__notes" v-if="notes">
						<i class="material-icons md-sm">class</i><span>Notes: {{ notes }}</span>
					</div>
					<!-- Customer Meta -->
					<div class="invoice__customer__item d-flex mt-4" v-if="customer.meta">
						<i class="material-icons md-sm">library_books</i>
						<div class="invoice__customer__metas">
							<div class="invoice__customer__meta" v-for="(item, index) in customer.meta" :key="index">
								<span>{{ item.label }}: </span>
								<span v-if="isFileUrl(item.value)">
									<a :href="item.value" target="_blank">{{ item.value }}</a>
								</span>
								<span v-else>{{ item.value }}</span>
							</div>
						</div>
					</div>
					<!-- Receipt Number -->
					<div class="invoice__customer__item invoice__receipt mt-4" v-if="receiptNumber !== ''">
						<i class="material-icons md-sm">local_shipping</i><span>Receipt Number: <b>{{ receiptNumber }}</b></span>
					</div>
				</div>
			</div>

			<!-- Items -->
			<div class="invoice__items">
				<div class="invoice__items__header">
					<div>Items</div>
					<div>Price</div>
				</div>
				<div class="invoice__items__list">
					<invoice-item
						v-for="(item, index) in items"
						:key="index"
						:item="item"
						:useLinkProduct="false"
						:mode="mode">
					</invoice-item>
				</div>
			</div>

			<!-- Total -->
			<div class="invoice__total" v-if="paymentVisible">
				<div class="invoice__total__label">Total</div>
				<div class="invoice__total__price">{{ formatMoney(total) }}</div>
			</div>
		</div>

		<!-- Footer -->
		<div class="invoice__footer">
			<div class="invoice__total" v-if="!paymentVisible">
				<div class="invoice__total__label">Total</div>
				<div class="invoice__total__price">{{ formatMoney(total) }}</div>
			</div>
			<div v-else>
				<div class="mb-3">Silahkan lakukan pembayaran atas invoice tersebut ke salah satu rekening berikut:</div>
				<div class="row">
					<div
						class="col-sm-6 col-md-4 mb-3"
						v-for="(bankAccount, index) in bankAccounts"
						:key="index">
						<div class="text-bold">Bank: {{ bankAccount.label }}</div>
						<div>No. Rek: <span class="text-bold">{{ bankAccount.account_number }}</span></div>
						<div>Atas Nama: {{ bankAccount.account_name }}</div>
					</div>
				</div>
			</div>
			<div class="invoice__copyright">
				<span>Powered By</span>
				<img :src="appLogo" alt="Ostock.id">
			</div>
		</div>
	</div>

	<!-- Loader -->
	<div class="invoice-loader is-loading large" v-else></div>
</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import InvoiceItem from './InvoiceItem.vue';
import {
	getStorefrontItems,
	imageElemtoBase64,
	getCourierServiceLabel,
} from '@/lib/helper';
import ordersApi from '@/api/submissions';
import { s3Url } from '@/lib/statics';

export default {
	name: 'Invoice',
	props: {
		store: {
			type: Object,
		},
		order: {
			type: Object,
		},
		timezone: {
			type: String,
			default: 'Asia/Jakarta',
		},
		paymentVisible: {
			type: Boolean,
			default: false,
		},
		courierVisible: {
			type: Boolean,
			default: false,
		},
		mode: {
			type: String,
			default: 'user',
		},
	},
	components: {
		InvoiceItem,
	},
	data() {
		return {
			orderDetail: null,
			// logoData: null,
		};
	},
	methods: {
		...mapActions({
			setLogoData: 'setLogoData',
			addOrderDetail: 'submissions/addDetail',
		}),
		setOrderDetail() {
			const orderId = this.order._id;
			const orderDetail = this.orderDetails.find(detail => detail._id === orderId);
			if (orderDetail) {
				this.orderDetail = orderDetail;
			} else {
				const callback = (response) => {
					const detail = response.data;
					this.addOrderDetail(detail);
					this.orderDetail = detail;
				};
				const errorCallback = () => {
					// eslint-disable-next-line
					this.$notify({
						group: 'orders',
						title: 'failed to get detail',
						text: 'failed',
						type: 'warn',
					});
				};
				const params = {
					is_deleted: this.order.is_deleted,
				};
				ordersApi.getDetailOrders(orderId, params, callback, errorCallback);
			}
		},
		onLogoLoaded(e) {
			if (this.logoData === '') {
				const img = e.target;
				const image = imageElemtoBase64(img);
				this.setLogoData(image.data);
			}
			// if (!this.logoData) {
			// 	const url = this.store.logo;
			// 	const callback = (response) => {
			// 		const imageData = response.data;
			// 		this.logoData = imageData;
			// 	};
			// 	imageURLtoBase64(url, callback);
			// }
		},
		isFileUrl(value) {
			const fileUrl = 's3-ap-southeast-1.amazonaws.com';
			return value && value.includes(fileUrl);
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			logoData: 'logoData',
			appLogo: 'appLogo',
			orderDetails: 'submissions/details',
		}),
		isAdminMode() {
			return this.mode === 'admin';
		},
		orderDate() {
			// return this.moment(this.order.created_at).format('DD-MM-YYYY');
			return this.moment.utc(this.order.created_at).tz(this.timezone).format('DD-MM-YYYY');
		},
		status() {
			return this.order.status;
		},
		paymentStatus() {
			return this.order.payment ? this.order.payment.status : 'paid';
		},
		customer() {
			return this.order.customer_data;
		},
		customerName() {
			return this.customer.name;
		},
		customerEmail() {
			return this.customer.email;
		},
		customerPhone() {
			return this.customer.phone;
		},
		customerAddress() {
			const address = this.customer.address;
			return address;
		},
		customerCity() {
			const state = this.customer.province_name ? this.customer.province_name : this.customer.state;
			const city = this.customer.city_name ? this.customer.city_name : this.customer.city;
			const district = this.customer.subdistrict_name ? this.customer.subdistrict_name : this.customer.district;
			const zip = this.customer.zip || '';
			// const city = this.customer.city ? this.customer.city : this.customer.city_name;
			// const district = this.customer.district ? this.customer.district : this.customer.subdistrict_name;
			// const state = this.customer.state ? this.customer.state : this.customer.province_name;
			const all = [];
			if (district) {
				all.push(district);
			}
			if (city) {
				all.push(city);
			}
			if (state) {
				all.push(state);
			}
			if (zip && zip !== '') {
				all.push(zip);
			}
			return all.join(', ');
		},
		total() {
			let total = this.order.gross_revenue;
			if (!total) {
				// eslint-disable-next-line camelcase
				total = this?.order?.total_price ?? 0;
			}
			return total;
		},
		items() {
			let items = [];
			if (!this.order.type || this.order.type === '' || this.order.type === 'form') {
				const productId = this.order.product_id;
				let productName = this.order.product_name;
				const customerData = this.order.customer_data;
				const quantity = this.order.customer_data.quantity;
				const price = this.order.product_price;

				if (!productName || productName === '') {
					// eslint-disable-next-line camelcase
					productName = this?.order?.product_data?.name ?? '';
				}

				const productMeta = this.customer.variation ? this.customer.variation.split(',') : [];
				const isHasMultipleVariations = customerData.variations && customerData.variations.length > 0;

				// Product
				const product = {
					name: productName,
					price,
					quantity,
					meta: productMeta,
					type: 'product',
					data: {
						productId,
					},
				};

				if (isHasMultipleVariations) {
					product.price = '';
					product.quantity = 0;
					delete product.meta;
				}

				items.push(product);

				// Multiple Variations
				if (isHasMultipleVariations) {
					customerData.variations.forEach((variation) => {
						const totalPrice = variation.price * variation.quantity;
						const rowItem = {
							type: 'variation',
							name: variation.value,
							price: totalPrice,
							quantity: variation.quantity,
							singlePrice: variation.price,
						};
						if (variation.wholesale_price) {
							const discountPrice = variation.wholesale_price * variation.quantity;
							rowItem.discountPrice = discountPrice;
						}
						items.push(rowItem);
					});
				}
			} else if (this.order.type === 'storefront') {
				if (this.orderDetail) {
					const productItems = getStorefrontItems(this.orderDetail);
					items = items.concat(productItems);
				}
			}

			// Bump
			if (this.order.bump.checked) {
				const bump = {
					name: this.order.bump.name,
					price: this.order.bump.price,
					type: 'bump',
				};

				items.push(bump);
			}

			// Shipping
			const orderShipping = this.order.shipping;
			const isShippingEnabled = orderShipping.courier && orderShipping.courier !== '';
			const isFlatShipping = orderShipping.courier === '' && orderShipping.cost !== 0;
			const isShowShipping = isShippingEnabled || isFlatShipping;
			const isCod = this.order.shipping.cod;
			const isCodIncludeShipping = isCod && this.order.shipping.cod_include_shipping;
			const isStorefrontCod = this.order.type === 'storefront' && isCod;

			if ((isShowShipping && !isCod) || isCodIncludeShipping || isStorefrontCod) {
				const shippingLabel = 'Shipping Cost';

				const isOriginProduct = this.order.shipping_origin && this.order.shipping_origin === 'product';

				orderShipping.service = getCourierServiceLabel(orderShipping?.courier, orderShipping.service);

				if (isOriginProduct && this.order.shipping_costs && this.order.shipping_costs.length > 0) {
					const shippingCosts = this.order.shipping_costs;
					for (let shippingCostsIndex = 0; shippingCostsIndex < shippingCosts.length; shippingCostsIndex++) {
						const shippingCostItem = shippingCosts[shippingCostsIndex];

						const courierName = shippingCostItem.courier || 'custom';
						const shippingCourier = courierName.toUpperCase();
						let shippingName = isShippingEnabled ? `${shippingLabel} (${shippingCourier} - ${orderShipping.service})` : shippingLabel;
						if (this.courierVisible) {
							shippingName = isShippingEnabled ? `${shippingLabel}` : shippingLabel;
						}
						if (isFlatShipping) {
							shippingName = `${shippingLabel} (Flat Shipping)`;
						}

						const shippingCost = shippingCostItem.cost;
						const shipping = {
							name: shippingName,
							price: shippingCost,
							type: 'shipping',
						};

						items.push(shipping);
					}
				} else {
					const courierName = orderShipping.courier || 'custom';
					const shippingCourier = courierName.toUpperCase();
					let shippingName = isShippingEnabled ? `${shippingLabel} (${shippingCourier} - ${orderShipping.service})` : shippingLabel;
					if (this.courierVisible) {
						shippingName = isShippingEnabled ? `${shippingLabel}` : shippingLabel;
					}
					if (isFlatShipping) {
						shippingName = `${shippingLabel} (Flat Shipping)`;
					}

					const shippingCost = this.order.shipping.cost;
					const shipping = {
						name: shippingName,
						price: shippingCost,
						type: 'shipping',
					};

					items.push(shipping);
				}
			}

			// COD
			if (isCod) {
				const codCost = this.order.shipping.cod_cost;
				const codItem = {
					name: 'COD Fee',
					price: codCost,
					type: 'shipping',
				};
				items.push(codItem);
			}

			// Reseller Array
			// const hasReseller = ;
			if (this.order && this.order.reseller) {
				let discount = 0;
				if (this.order.reseller.length > 0) {
					discount = this.order.reseller.filter(res => res.commission_type === 'discount').reduce((sum, dc) => sum + dc.discount * dc.quantity, 0);
				} else if (this.order.reseller.commission_type === 'discount') {
					discount = this.order.reseller.discount * this.order.reseller.quantity;
				}
				const resellerName = 'Potongan Reseller';
				const resellerPrice = discount * -1;
				const reseller = {
					name: resellerName,
					price: resellerPrice,
					type: 'reseller',
				};
				if (resellerPrice < 0) {
					items.push(reseller);
				}
			}

			// Coupon
			const isHasCoupon = this.order && this.order.coupon && this.order.coupon.code && this.order.coupon.code !== '';
			if (isHasCoupon) {
				const couponCode = this.order.coupon.code;
				const couponName = `Discount (${couponCode})`;
				const couponPrice = this.order.coupon.discount * -1;
				const coupon = {
					name: couponName,
					price: couponPrice,
					type: 'coupon',
				};

				items.push(coupon);
			}

			// Unique Code
			const isHasUniqueCode = this.order.unique_code_price.unique_code !== 0;
			if (isHasUniqueCode) {
				const uniqueCode = this.order.unique_code_price.unique_code;
				const uniqueCodePrice = (this.order.unique_code_price.type === 'decrease') ? uniqueCode * -1 : uniqueCode;
				const uniqueCodeData = {
					name: 'Unique Code',
					price: uniqueCodePrice,
					type: 'unique_code',
				};

				items.push(uniqueCodeData);
			}

			// Other Cost
			if (this.order.other_cost) {
				const otherCost = {
					name: 'Other Cost',
					price: this.order.other_cost,
					type: 'other',
				};
				items.push(otherCost);
			}

			return items;
		},
		quantity() {
			return this.order.customer_data.quantity;
		},
		notes() {
			return this.order.customer_data.notes;
		},
		receiptNumber() {
			return this.order.shipping.receipt_number ? this.order.shipping.receipt_number : '';
		},
		storeName() {
			return this.store ? this.store.name : 'LOGO';
		},
		logo() {
			let logoUrl = this.store ? this.store.logo : '';
			logoUrl = logoUrl.replace('https://cdn.ostock.id', s3Url);
			if (this.logoData && this.logoData !== '') {
				logoUrl = this.logoData;
			}
			return logoUrl;
		},
		bankAccounts() {
			const bankAccounts = this.user.bank_accounts || [];
			return bankAccounts;
		},
	},
	created() {
		if (this.order && this.order.type === 'storefront') {
			this.setOrderDetail();
		}
	},
	watch: {
		order() {
			if (this.order && this.order.type === 'storefront') {
				this.setOrderDetail();
			}
		},
	},
};
</script>

<style lang="scss">
@import '../assets/scss/modules/_variables.scss';

.invoice-wrapper {
	border-radius: 3px;
	overflow: hidden;
}

.invoice {
	color: #000;
	background-color: #fff;
}

.invoice-loader {
	height: 8em;
}

.invoice__body {
	padding: 3em 3.5em;
	overflow-wrap: break-word;

	@media (max-width: 767px) {
		padding: 2em;
	}
}

.invoice__footer {
	padding: 1em 3.5em;
	background-color: $color__row;

	@media (max-width: 767px) {
		padding: 1em 2em;
	}
}

.invoice__header {
	display: flex;
	justify-content: space-between;
}

.invoice__logo {
	width: 100%;
	max-width: 175px;
	height: auto;
}

.invoice__title {
	font-size: 1.5em;
	font-weight: bold;
	text-align: right;
	text-transform: uppercase;
}

.invoice__customer {
	margin-top: 1em;

	a {
		vertical-align: middle;
	}

	.material-icons {
		top: 0;
		margin-right: .5em;
		vertical-align: middle;
	}
}

.invoice__customer__item {
	margin-bottom: .5em;
}

.invoice__customer__top {
	display: flex;
	margin-bottom: .5em;
	font-size: 1.2em;
	justify-content: space-between;
}

.invoice__customer__name {
	padding-right: 1em;
	max-width: 75%;
}

.invoice-status {
	padding: .2em .5em;
	color: #777;
	font-weight: bold;
	border: 3px solid #e5e5e5;
	text-transform: uppercase;

	&.pending {
		color: #94660c;
		border-color: #f8dda7;
	}

	&.processing
	&.unpaid {
		color: #2e4453;
		border-color: #c8d7e1;
	}

	&.completed,
	&.paid {
		color: #5b841b;
		border-color: #c6e1c6;
	}

	&.unpaid {
		background-color: #efefef;
	}
}

.invoice-order-status {
	text-transform: uppercase;
}

.invoice__customer__phone {
	span {
		vertical-align: middle;
	}
}

.invoice__customer__address {
	display: flex;

	span {
		vertical-align: middle;
	}
}

.invoice__customer__street {
	margin-bottom: .5em;
}

// .invoice__customer__city {
	// margin-top: .5em;
// }

.invoice__customer__notes {
	margin-top: 1em;
}

.invoice__items {
	margin-top: 2em;
}

.invoice__items__header {
	display: flex;
	margin-bottom: .7em;
	padding-bottom: .3em;
	font-size: 1.2em;
	font-weight: bold;
	border-bottom: 1px solid #ddd;
	justify-content: space-between;
}

.invoice__item {
	display: flex;
	margin-bottom: .5em;
	padding-bottom: .5em;
	border-bottom: 1px dashed #bbb;
	justify-content: space-between;
}

.invoice__item--small {
	font-size: .9em;
}

.invoice__item__price {
	min-width: 21%;
	font-weight: bold;
	text-align: right;
}

.invoice__total {
	display: flex;
	font-size: 1.2em;
	font-weight: bold;
	justify-content: space-between;
}

.invoice__total__price {
	color: $color__primary;
}

.invoice__copyright {
	margin-top: 1em;
	margin-bottom: .5em;
	color: #bbb;
	font-size: .9em;
	font-weight: bold;
	font-style: italic;
	text-align: center;

	span {
		display: inline-block;
		font-size: .9em;
		vertical-align: middle;
	}

	img {
		display: inline-block;
		margin-left: 1em;
		max-width: 125px;
		vertical-align: middle;
	}
}

.invoice__item__metas {
	font-size: .85em;
	margin-top: .2em;
}
</style>
